import type { ArrayFillSuggestion } from './array-fill-suggestion.js';
import { GREEK_FIRST_NAMES, GREEK_SURNAMES } from './greek-names-data.js';

/**
 * Παραγωγή ΤΥΧΑΙΩΝ, έγκυρων τιμών εισόδου (ως ΚΕΙΜΕΝΟ — ό,τι θα πληκτρολογούσε
 * ο μαθητής σε ένα ΔΙΑΒΑΣΕ) για μια `ArrayFillSuggestion` — βλ.
 * `array-fill-suggestion.ts` για την (καθαρή, χωρίς τυχαιότητα) στατική
 * ανίχνευση. Ξεχωριστό αρχείο ώστε η ανίχνευση να μένει ντετερμινιστική/
 * εύκολα ελέγξιμη, ενώ η τυχαιότητα ζει μόνο εδώ.
 *
 * Όταν το σχήμα ΔΕΝ αναγνωρίστηκε (`pattern.kind === 'unconstrained'`) η
 * τιμή παράγεται μόνο από τον ΤΥΠΟ — δεν εγγυάται εγκυρότητα απέναντι σε
 * τυχόν συνθήκη που δεν αναγνωρίσαμε· ο ήδη υπάρχων βρόχος ελέγχου
 * εγκυρότητας του ίδιου του προγράμματος θα την απορρίψει και θα ξαναζητήσει
 * κανονικά αν χρειαστεί (το κουμπί απλά συνεχίζει να απαντάει τυχαία σε κάθε
 * επόμενο αίτημα εισόδου, βλ. web/src/main.ts).
 *
 * Το `ArrayFillOverride` (προαιρετικό) αντικαθιστά ΠΛΗΡΩΣ το αυτόματα
 * ανιχνευμένο `pattern` — ό,τι επέλεξε ρητά ο μαθητής στο πλαίσιο ρυθμίσεων
 * (⚙️, βλ. web/src/main.ts) έχει πάντα προτεραιότητα.
 */

/** Οι 3 κατηγορίες που μπορεί να διαλέξει ρητά ο μαθητής στο πλαίσιο ρυθμίσεων (⚙️) ενός πίνακα ΧΑΡΑΚΤΗΡΕΣ. */
export type CharacterAlphabet = 'firstNames' | 'surnames' | 'numbers';

export type ArrayFillOverride =
  | { readonly kind: 'range'; readonly min: number; readonly max: number; readonly decimals: number }
  | { readonly kind: 'set'; readonly values: readonly (number | string | boolean)[] }
  | { readonly kind: 'alphabet'; readonly alphabet: CharacterAlphabet };

/** Όριο μεγέθους για αριθμούς όταν η συνθήκη δεν δίνει ρητό άνω/κάτω όριο — αρκετά μικρό ώστε οι τιμές να παραμένουν ευανάγνωστες. */
const DEFAULT_BOUND_MAGNITUDE = 100;
/** Εσωτερική προεπιλογή για ΧΑΡΑΚΤΗΡΕΣ όταν ΔΕΝ δόθηκε `override` ΚΑΙ δεν αναγνωρίστηκε σύνολο τιμών — δεν εκτίθεται σαν επιλογή στο ⚙️ (εκεί ζητείται πάντα ρητή κατηγορία, βλ. web/src/main.ts). */
const GREEK_UPPERCASE_LETTERS = 'ΑΒΓΔΕΖΗΘΙΚΛΜΝΞΟΠΡΣΤΥΦΧΨΩ';

function randomInt(min: number, max: number): number {
  if (max < min) return min;
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function randomReal(min: number, max: number, decimals: number): number {
  if (max < min) return min;
  return roundTo(Math.random() * (max - min) + min, decimals);
}

function randomFromSet<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)]!;
}

function randomIntegerInRange(min: number | null, minInclusive: boolean, max: number | null, maxInclusive: boolean): number {
  const effectiveMin = min === null ? -DEFAULT_BOUND_MAGNITUDE : minInclusive ? Math.ceil(min) : Math.floor(min) + 1;
  const effectiveMax = max === null ? DEFAULT_BOUND_MAGNITUDE : maxInclusive ? Math.floor(max) : Math.ceil(max) - 1;
  return randomInt(effectiveMin, effectiveMax);
}

function randomRealInRange(min: number | null, minInclusive: boolean, max: number | null, maxInclusive: boolean): number {
  const EPSILON = 0.01;
  const effectiveMin = min === null ? -DEFAULT_BOUND_MAGNITUDE : minInclusive ? min : min + EPSILON;
  const effectiveMax = max === null ? DEFAULT_BOUND_MAGNITUDE : maxInclusive ? max : max - EPSILON;
  return randomReal(effectiveMin, effectiveMax, 2);
}

function randomFromAlphabet(alphabet: CharacterAlphabet): string {
  switch (alphabet) {
    case 'firstNames':
      return randomFromSet(GREEK_FIRST_NAMES);
    case 'surnames':
      return randomFromSet(GREEK_SURNAMES);
    case 'numbers':
      return String(randomInt(0, 999));
  }
}

function randomUnconstrained(elementType: ArrayFillSuggestion['elementType']): number | string | boolean {
  switch (elementType) {
    case 'ΑΚΕΡΑΙΕΣ':
      return randomInt(-DEFAULT_BOUND_MAGNITUDE, DEFAULT_BOUND_MAGNITUDE);
    case 'ΠΡΑΓΜΑΤΙΚΕΣ':
      return randomReal(-DEFAULT_BOUND_MAGNITUDE, DEFAULT_BOUND_MAGNITUDE, 2);
    case 'ΧΑΡΑΚΤΗΡΕΣ':
      return randomFromSet([...GREEK_UPPERCASE_LETTERS]);
    case 'ΛΟΓΙΚΕΣ':
      return Math.random() < 0.5;
  }
}

/**
 * Μία τυχαία τιμή για την πρόταση — βλ. σχόλιο αρχείου. Αν δοθεί `override`,
 * αγνοεί ΕΝΤΕΛΩΣ το αυτόματα ανιχνευμένο `pattern` και χρησιμοποιεί αυτό.
 */
export function generateArrayFillValue(suggestion: ArrayFillSuggestion, override?: ArrayFillOverride): number | string | boolean {
  if (override !== undefined) {
    switch (override.kind) {
      case 'set':
        return randomFromSet(override.values);
      case 'range':
        return override.decimals > 0 ? randomReal(override.min, override.max, override.decimals) : randomInt(Math.ceil(override.min), Math.floor(override.max));
      case 'alphabet':
        return randomFromAlphabet(override.alphabet);
    }
  }
  const { pattern, elementType } = suggestion;
  if (pattern.kind === 'set') return randomFromSet(pattern.values);
  if (pattern.kind === 'range') {
    return elementType === 'ΠΡΑΓΜΑΤΙΚΕΣ'
      ? randomRealInRange(pattern.min, pattern.minInclusive, pattern.max, pattern.maxInclusive)
      : randomIntegerInRange(pattern.min, pattern.minInclusive, pattern.max, pattern.maxInclusive);
  }
  return randomUnconstrained(elementType);
}

/** Η μορφή κειμένου που θα περίμενε το ΔΙΑΒΑΣΕ αν ο μαθητής την είχε πληκτρολογήσει ο ίδιος. */
export function generateArrayFillInputText(suggestion: ArrayFillSuggestion, override?: ArrayFillOverride): string {
  const value = generateArrayFillValue(suggestion, override);
  if (typeof value === 'boolean') return value ? 'ΑΛΗΘΗΣ' : 'ΨΕΥΔΗΣ';
  return String(value);
}
